import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import qs from 'query-string';

import useUserback from '../../../../hooks/useUserback';

import styles from './CampaignsPage.module.css';
import Footer from '../Footer/Footer';
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';
import UserDetails from './components/UserDetails/UserDetails';
import StepsProgress from '../StepsProgress/StepsProgress';
import AcceptTermsInput from '../../../../form/AcceptTermsInput/AcceptTermsInput';
import EndSurveyImages from '../EndSurveyImages/EndSurveyImages';
import TranslationInputField from '../TranslationInputField/TranslationInputField';

import dutchLanguageSphere from '../../../../assets/img/dutch-language-sphere.svg';
import frenchLanguageSphere from '../../../../assets/img/french-language-sphere.svg';
import englishLanguageSphere from '../../../../assets/img/english-language-sphere.svg';
import GooglePlayStore from '../../../../assets/img/play-store.png';
import AppleStore from '../../../../assets/img/app-store.png';

import {
  CUSTOM_THEME_COLOR_NAMES,
  CUSTOM_THEME_IMAGE_NAMES
} from '../../../../../clients/surveys/helpers/constants';

import { GET_APP_CONFIGURATION } from '../../../../graphql/Auth';
import { GET_CAMPAIGN, START_WEB_SURVEY } from '../../../../graphql/Campaigns';

export default props => {
  const {
    location: { search },
    match,
    strings,
    history,
    setCampaignComponents,
    result,
    block,
    setHideBufflLogo,
    customTranslationsLoaded,
    setCustomTranslations,
    selectedLanguage,
    setSelectedLanguage,
    translations,
    updateTranslationsLanguage,
    setCustomThemeBaseColor,
    setCustomThemeImage,
    setUserEmailSuggestion,
    savingTranslationError,
    setSavingTranslationError,
    clientId
  } = props;

  const pages = {
    TERMS: 'TERMS',
    USER_DETAILS: 'USER_DETAILS',
    CAMPAIGN: 'CAMPAIGN',
    READY: 'READY',
    NOT_AVAILABLE: 'NOT_AVAILABLE'
  };

  const languages = {
    ENGLISH: 'English',
    DUTCH: 'Dutch',
    FRENCH: 'French',
    GERMAN: 'German'
  };

  const languageKeys = {
    English: 'EN',
    Dutch: 'NL',
    French: 'FR',
    German: 'DE'
  };

  const customCopyFields = {
    welcome: {
      name: 'welcome',
      fields: {
        WELCOME: 'WELCOME',
        WE_MAKE_YOU_PLEASANT_HERE: 'WE_MAKE_YOU_PLEASANT_HERE',
        GIVE_YOUR_OPINION_THERE_ARE_NO_BAD_ANSWERS:
          'GIVE_YOUR_OPINION_THERE_ARE_NO_BAD_ANSWERS'
      }
    },
    intro: {
      name: 'intro',
      fields: {
        INTRODUCTION: 'INTRODUCTION'
      }
    },
    notAvailable: {
      name: 'notAvailable',
      fields: {
        UNFORTUNATELY_ON_NOT_AVAILABLE_PAGE:
          'UNFORTUNATELY_ON_NOT_AVAILABLE_PAGE',
        THE_SURVEY_YOU_ARE_LOOKING_IS_NO_AVAILABLE:
          'THE_SURVEY_YOU_ARE_LOOKING_IS_NO_AVAILABLE',
        THE_SURVEY_YOU_ARE_LOOKING_IS_UNFORTUNATELY_NO_AVAILABLE:
          'THE_SURVEY_YOU_ARE_LOOKING_IS_UNFORTUNATELY_NO_AVAILABLE',
        DO_NOT_WORRY_DOZENS_OF_RESEARCHES_NOT_AVAILABLE:
          'DO_NOT_WORRY_DOZENS_OF_RESEARCHES_NOT_AVAILABLE'
      }
    }
  };

  useUserback({
    ...match.params
  });

  const [showContent, setShowContent] = useState(false);
  const [campaignStarted, setCampaignStarted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [viewTerms, setViewTerms] = useState(false);
  const [userDetailsCompleted, setUserDetailsCompleted] = useState(false);
  const [userDetails, setUserDetails] = useState({
    createProfile: false,
    language: selectedLanguage
  });
  const [localCustomCopies, setLocalCustomCopies] = useState([]);
  const [resizeTranslationField, setResizeTranslationField] = useState(false);

  const {
    data: { getAppConfiguration } = {},
    loading: loadingAppConfiguration
  } = useQuery(GET_APP_CONFIGURATION, {
    fetchPolicy: 'network-only'
  });

  const goToFailedPage = () => {
    const { customClientIdentifier, campaignId, userId } = match.params;

    let finalUrl;
    if (customClientIdentifier) {
      const redirectUrl = campaign.redirectionsWebSurvey.campaignQuotaFull;

      if (redirectUrl && redirectUrl !== '') {
        const urlCustomIdentifierSign = redirectUrl.includes('?') ? '&' : '?';
        finalUrl = `${redirectUrl}${urlCustomIdentifierSign}m=${
          match.params.customClientIdentifier
        }`;
      } else {
        finalUrl = `/users/campaign/${campaignId}/user/${userId}/campaign-failed`;
      }

      window.location.href = encodeURI(finalUrl);
    } else {
      finalUrl = `/campaign/${campaignId}/user/${userId}/campaign-failed`;
    }
    if (isTesting()) {
      finalUrl = `${finalUrl}?test=true`;
    }

    history.push(finalUrl);
  };

  const [startWebSurvey, { startWebSurveyProcessing }] = useMutation(
    START_WEB_SURVEY,
    {
      onCompleted: response => {
        const startWebSurveyResult = response.startWebSurvey;

        if (startWebSurveyResult && startWebSurveyResult.failure) {
          // Go to quota full page
          goToFailedPage();
        } else {
          setCampaignComponents(
            campaign,
            startWebSurveyResult.nextBlock,
            startWebSurveyResult.result
          );
        }
      },
      onError: () => {
        // Go to quota full page
        goToFailedPage();
      }
    }
  );

  const { data: { campaign } = {}, loading: loadingCampaign } = useQuery(
    GET_CAMPAIGN,
    {
      variables: { id: match.params.campaignId, referrer: match.params.userId },
      onCompleted: () => setShowContent(true),
      onError: () => setShowContent(true),
      fetchPolicy: 'network-only'
    }
  );

  const isTesting = () => search === '?test=true';

  const customThemeBaseColor =
    campaign &&
    campaign.customBranding &&
    campaign.customThemeColors &&
    campaign.customThemeColors.length &&
    campaign.customThemeColors.find(
      cT => cT.name === CUSTOM_THEME_COLOR_NAMES.BASE
    ).colorCode;

  const customThemeImage =
    campaign &&
    campaign.customBranding &&
    campaign.customThemeImages &&
    campaign.customThemeImages.length;

  const customThemeLogoName =
    campaign &&
    campaign.customBranding &&
    campaign.customThemeImages &&
    campaign.customThemeImages.length &&
    campaign.customThemeImages.find(
      cT => cT.name === CUSTOM_THEME_IMAGE_NAMES.LOGO
    ).name;

  const customThemeLogoImage =
    campaign &&
    campaign.customBranding &&
    campaign.customThemeImages &&
    campaign.customThemeImages.length &&
    campaign.customThemeImages.find(
      cT => cT.name === CUSTOM_THEME_IMAGE_NAMES.LOGO
    ).imageFilename;

  const allowedToEditTranslations =
    campaign &&
    isTesting() &&
    clientId &&
    ((campaign.client && campaign.client === clientId) ||
      (campaign.collaborators &&
        campaign.collaborators.length &&
        campaign.collaborators.find(c => c.id === clientId)));

  const translationInputFieldProps = campaign
    ? {
        surveyId: campaign.id,
        clientId,
        language: selectedLanguage || campaign.language,
        disabled: !allowedToEditTranslations || savingTranslationError,
        testing: isTesting(),
        setSavingTranslationError,
        resizeTranslationField,
        setResizeTranslationField
      }
    : {};

  useEffect(() => {
    if (selectedLanguage) {
      localStorage.setItem('campaignLanguage', selectedLanguage);
    }
    const query = qs.parse(search);
    if (
      query &&
      query.userEmailSuggestion &&
      query.userEmailSuggestion.length
    ) {
      setUserEmailSuggestion(query.userEmailSuggestion);
    }
  }, []);

  useEffect(
    () => {
      if (campaign && campaign.language) {
        localStorage.setItem('campaignLanguage', campaign.language);
        setSelectedLanguage(campaign.language);
        if (
          campaign &&
          !(campaign.customCopies && campaign.customCopies.length)
        ) {
          updateTranslationsLanguage(campaign.language || selectedLanguage);
        }
      }
      if (campaign && campaign.hideLogoInWebSurvey && campaign.customBranding)
        setHideBufflLogo(true);
      if (
        !customTranslationsLoaded &&
        campaign &&
        campaign.customCopies &&
        campaign.customCopies.length
      ) {
        setCustomTranslations(
          campaign.language || selectedLanguage,
          campaign.customCopies
        );
        setLocalCustomCopies(campaign.customCopies);
      }
      if (customThemeBaseColor) setCustomThemeBaseColor(customThemeBaseColor);
      if (customThemeImage) {
        setCustomThemeImage({
          NAME: customThemeLogoName,
          IMAGE: customThemeLogoImage
        });
      }
    },
    [campaign]
  );

  const steps = [
    translations.WELCOME_STEP,
    ...(campaign && campaign.askForSocioDemoWebSurvey
      ? [translations.YOUR_INFO]
      : []),
    translations.SURVEY,
    translations.USER_PROFILE,
    translations.CONFIRMATION
  ];

  const goToUrl = url => {
    let finalUrl = url;
    if (isTesting()) {
      finalUrl = `${url}?test=true`;
    }

    props.history.push(finalUrl);
  };

  const startCampaignQueestions = () => {
    if (match.params.customClientIdentifier) {
      goToUrl(
        `/result/${result.id}/block/${block.id}/user/${
          props.match.params.userId
        }/${match.params.customClientName}/${
          match.params.customClientIdentifier
        }`
      );
    } else {
      goToUrl(
        `/result/${result.id}/block/${block.id}/user/${
          props.match.params.userId
        }`
      );
    }
    document.documentElement.scrollTop = 0;
  };

  const changeLanguage = language => {
    setSelectedLanguage(language);
    localStorage.setItem('campaignLanguage', language);
    if (
      campaign &&
      ((campaign.customCopies && campaign.customCopies.length) ||
        (localCustomCopies && localCustomCopies.length))
    ) {
      setCustomTranslations(
        language,
        localCustomCopies && localCustomCopies.length
          ? localCustomCopies
          : campaign.customCopies
      );
      setResizeTranslationField(customCopyFields.welcome.name);
    } else {
      updateTranslationsLanguage(language);
    }

    setUserDetails({
      ...userDetails,
      language
    });
  };

  const showPage = page => {
    switch (page) {
      case pages.NOT_AVAILABLE:
        return (
          !campaign ||
          campaign.status === 'finished' ||
          (campaign.published === 'false' && !isTesting())
        );
      case pages.CAMPAIGN:
        return !campaignStarted;
      case pages.USER_DETAILS:
        return (
          campaign &&
          ((!result && result !== false) || !userDetailsCompleted) &&
          campaignStarted
        );
      case pages.TERMS:
        return campaign && result && campaignStarted && viewTerms;
      case pages.READY:
        return campaign && result && campaignStarted && userDetailsCompleted;
      default:
        return null;
    }
  };

  const onUpdateLocalCustomCopy = (key, value, name) => {
    let customCopyParentGroup =
      localCustomCopies && localCustomCopies.find(copy => copy.name === name);

    const customCopies = [...localCustomCopies];

    let newTranslation = {
      KEY: key,
      [languageKeys[selectedLanguage]]: value
    };

    if (customCopyParentGroup) {
      customCopies.splice(
        customCopies.findIndex(copy => copy.name === name),
        1
      );
      const cTranslation = customCopyParentGroup.translations.find(
        t => t.KEY === key
      );
      if (cTranslation) {
        newTranslation = {
          ...cTranslation,
          [languageKeys[selectedLanguage]]: value
        };
        const cTranslationIndex = customCopyParentGroup.translations.findIndex(
          t => t.KEY === key
        );
        customCopyParentGroup.translations.splice(cTranslationIndex, 1);
      }
      customCopyParentGroup.translations.push(newTranslation);
    } else {
      customCopyParentGroup = {
        active: true,
        name,
        translations: [newTranslation]
      };
    }

    setCustomTranslations(selectedLanguage || campaign.language, [
      ...customCopies,
      customCopyParentGroup
    ]);
    setLocalCustomCopies([...customCopies, customCopyParentGroup]);
  };

  if (startWebSurveyProcessing || loadingCampaign) return null;

  if (!loadingAppConfiguration && !loadingCampaign && showContent) {
    if (showPage(pages.NOT_AVAILABLE)) {
      return (
        <div
          className={`${styles.notAvailableContainer} ${
            campaign && campaign.hideDownloadBufflInWebSurvey
              ? styles.notAvailableContainerDownloadHidden
              : ''
          }`}
        >
          <EndSurveyImages
            page={pages.NOT_AVAILABLE}
            hideDownloadBufflInWebSurvey={
              campaign && campaign.hideDownloadBufflInWebSurvey
            }
            hideIllustrationsInWebSurvey={
              campaign &&
              campaign.hideIllustrationsInWebSurvey &&
              campaign.customBranding
            }
            customThemeBaseColor={customThemeBaseColor}
          />
          <div
            className={
              campaign && campaign.hideDownloadBufflInWebSurvey
                ? `${styles.mainContainer} ${
                    styles.mainContainerDownloadHidden
                  }`
                : styles.mainContainer
            }
          >
            <div className={styles.notAvailableContentContainer}>
              <TranslationInputField
                name={customCopyFields.notAvailable.name}
                translationKey={
                  campaign && campaign.hideDownloadBufflInWebSurvey
                    ? customCopyFields.notAvailable.fields
                        .UNFORTUNATELY_ON_NOT_AVAILABLE_PAGE
                    : customCopyFields.notAvailable.fields
                        .THE_SURVEY_YOU_ARE_LOOKING_IS_UNFORTUNATELY_NO_AVAILABLE
                }
                value={
                  campaign && campaign.hideDownloadBufflInWebSurvey
                    ? translations[
                        customCopyFields.notAvailable.fields
                          .UNFORTUNATELY_ON_NOT_AVAILABLE_PAGE
                      ]
                    : translations[
                        customCopyFields.notAvailable.fields
                          .THE_SURVEY_YOU_ARE_LOOKING_IS_UNFORTUNATELY_NO_AVAILABLE
                      ]
                }
                customClasses={`${styles.notAvailableTitle} ${
                  campaign && campaign.hideDownloadBufflInWebSurvey
                    ? styles.hideDownloadTitle
                    : ''
                } campaign-not-available`}
                {...translationInputFieldProps}
              />

              <TranslationInputField
                name={customCopyFields.notAvailable.name}
                translationKey={
                  campaign && campaign.hideDownloadBufflInWebSurvey
                    ? customCopyFields.notAvailable.fields
                        .THE_SURVEY_YOU_ARE_LOOKING_IS_NO_AVAILABLE
                    : customCopyFields.notAvailable.fields
                        .DO_NOT_WORRY_DOZENS_OF_RESEARCHES_NOT_AVAILABLE
                }
                value={
                  campaign && campaign.hideDownloadBufflInWebSurvey
                    ? translations[
                        customCopyFields.notAvailable.fields
                          .THE_SURVEY_YOU_ARE_LOOKING_IS_NO_AVAILABLE
                      ]
                    : translations[
                        customCopyFields.notAvailable.fields
                          .DO_NOT_WORRY_DOZENS_OF_RESEARCHES_NOT_AVAILABLE
                      ]
                }
                customClasses={`${styles.notAvailableDescription}`}
                {...translationInputFieldProps}
              />

              {!(campaign && campaign.hideDownloadBufflInWebSurvey) ? (
                <div className={styles.appStoreButtons}>
                  <a
                    target="_blank"
                    href="https://play.google.com/store/apps/details?id=org.nativescript.buffl"
                    rel="noopener noreferrer"
                  >
                    <img src={GooglePlayStore} alt="Google Play Store Button" />
                  </a>
                  <a
                    target="_blank"
                    href="https://itunes.apple.com/be/app/buffl/id1356715033?l=nl&mt=8"
                    rel="noopener noreferrer"
                  >
                    <img src={AppleStore} alt="App Store Button" />
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    if (showPage(pages.CAMPAIGN)) {
      return (
        <div className={styles.parentContainer} key="page-content">
          <div className={styles.mainContainer}>
            <StepsProgress
              activeStep="0"
              steps={steps}
              customThemeBaseColor={customThemeBaseColor}
            />
            <TranslationInputField
              name={customCopyFields.welcome.name}
              translationKey={customCopyFields.welcome.fields.WELCOME}
              value={translations[customCopyFields.welcome.fields.WELCOME]}
              customClasses={`${styles.campaignName} campaign-not-available`}
              {...translationInputFieldProps}
              onUpdateLocalCustomCopy={(key, value, name) =>
                onUpdateLocalCustomCopy(key, value, name)
              }
            />

            <div className={styles.campaignDescriptionContainer}>
              <div className={styles.campaignDescriptionFlex}>
                <TranslationInputField
                  name={customCopyFields.welcome.name}
                  translationKey={
                    !campaign.language
                      ? customCopyFields.welcome.fields
                          .WE_MAKE_YOU_PLEASANT_HERE
                      : customCopyFields.welcome.fields
                          .GIVE_YOUR_OPINION_THERE_ARE_NO_BAD_ANSWERS
                  }
                  value={
                    !campaign.language
                      ? translations[
                          customCopyFields.welcome.fields
                            .WE_MAKE_YOU_PLEASANT_HERE
                        ]
                      : translations[
                          customCopyFields.welcome.fields
                            .GIVE_YOUR_OPINION_THERE_ARE_NO_BAD_ANSWERS
                        ]
                  }
                  customClasses={`${styles.campaignDescriptionTranslation}`}
                  {...translationInputFieldProps}
                  onUpdateLocalCustomCopy={(key, value, name) =>
                    onUpdateLocalCustomCopy(key, value, name)
                  }
                />

                <div className={styles.languagesSelectorContainer}>
                  {!campaign.language ? (
                    <>
                      <div
                        role="presentation"
                        onClick={() => changeLanguage(languages.DUTCH)}
                        className={styles.languageSelector}
                        style={{
                          // eslint-disable-next-line
                          backgroundColor: customThemeBaseColor
                            ? selectedLanguage === languages.DUTCH
                              ? customThemeBaseColor
                              : `${customThemeBaseColor}10`
                            : selectedLanguage === languages.DUTCH
                            ? '#e4dbfc'
                            : '#f9f7fe'
                        }}
                      >
                        <span
                          className={styles.languageSelectorDesktopContent}
                          style={{
                            // eslint-disable-next-line
                            color: customThemeBaseColor
                              ? selectedLanguage === languages.DUTCH
                                ? '#FFFFFF'
                                : customThemeBaseColor
                              : '#5200f1'
                          }}
                        >
                          NL
                        </span>
                        <div className={styles.languageSelectorMobileContent}>
                          <img
                            src={dutchLanguageSphere}
                            alt="Dutch flag sphere"
                            className={styles.languageSphere}
                          />
                          <span>Nederlands</span>
                        </div>
                      </div>

                      <div
                        role="presentation"
                        onClick={() => changeLanguage(languages.FRENCH)}
                        className={styles.languageSelector}
                        style={{
                          // eslint-disable-next-line
                          backgroundColor: customThemeBaseColor
                            ? selectedLanguage === languages.FRENCH
                              ? customThemeBaseColor
                              : `${customThemeBaseColor}10`
                            : selectedLanguage === languages.FRENCH
                            ? '#e4dbfc'
                            : '#f9f7fe'
                        }}
                      >
                        <span
                          className={styles.languageSelectorDesktopContent}
                          style={{
                            // eslint-disable-next-line
                            color: customThemeBaseColor
                              ? selectedLanguage === languages.FRENCH
                                ? '#FFFFFF'
                                : customThemeBaseColor
                              : '#5200f1'
                          }}
                        >
                          FR
                        </span>
                        <div className={styles.languageSelectorMobileContent}>
                          <img
                            src={frenchLanguageSphere}
                            alt="French flag sphere"
                            className={styles.languageSphere}
                          />
                          <span>Français</span>
                        </div>
                      </div>
                      <div
                        role="presentation"
                        onClick={() => changeLanguage(languages.ENGLISH)}
                        className={styles.languageSelector}
                        style={{
                          // eslint-disable-next-line
                          backgroundColor: customThemeBaseColor
                            ? selectedLanguage === languages.ENGLISH
                              ? customThemeBaseColor
                              : `${customThemeBaseColor}10`
                            : selectedLanguage === languages.ENGLISH
                            ? '#e4dbfc'
                            : '#f9f7fe'
                        }}
                      >
                        <span
                          className={styles.languageSelectorDesktopContent}
                          style={{
                            // eslint-disable-next-line
                            color: customThemeBaseColor
                              ? selectedLanguage === languages.ENGLISH
                                ? '#FFFFFF'
                                : customThemeBaseColor
                              : '#5200f1'
                          }}
                        >
                          ENG
                        </span>
                        <div className={styles.languageSelectorMobileContent}>
                          <img
                            src={englishLanguageSphere}
                            alt="English flag sphere"
                            className={styles.languageSphere}
                          />
                          <span>English</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className={styles.startSelector}
                      style={
                        customThemeBaseColor
                          ? {
                              backgroundColor: customThemeBaseColor,
                              color: '#FFFFFF'
                            }
                          : null
                      }
                      role="presentation"
                      onClick={() => setCampaignStarted(true)}
                    >
                      {translations.START_SURVEY}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            <Footer
              onClickNext={() => setCampaignStarted(true)}
              disabledNext={false}
              hiddenNext={campaign.language}
              nextLabel={translations.NEXT}
              language={selectedLanguage}
              languages={languages}
              page={pages.CAMPAIGN}
              pages={pages}
              campaignLanguage={campaign.language}
              fixedPosition={!!campaign.language}
              hideIllustrationsInWebSurvey={
                campaign.hideIllustrationsInWebSurvey && campaign.customBranding
              }
              customThemeBaseColor={customThemeBaseColor}
            />
          </div>
        </div>
      );
    }

    if (showPage(pages.TERMS)) {
      return (
        <TermsAndConditions
          termsContent={getAppConfiguration.legal.summary}
          campaign={campaign}
          match={match}
          strings={strings}
          languages={languages}
          selectedLanguage={selectedLanguage}
          changeLanguage={changeLanguage}
          setViewTerms={setViewTerms}
          steps={steps}
          askForSocioDemoWebSurvey={campaign.askForSocioDemoWebSurvey}
          customThemeBaseColor={customThemeBaseColor}
        />
      );
    }

    if (showPage(pages.USER_DETAILS)) {
      return (
        <UserDetails
          translations={translations}
          campaign={campaign}
          match={match}
          strings={strings}
          startWebSurvey={startWebSurvey}
          choices={getAppConfiguration}
          userId={props.match.params.userId}
          history={history}
          isTesting={isTesting}
          page={pages.USER_DETAILS}
          pages={pages}
          onClickBack={() => {
            setCampaignStarted(false);
          }}
          userDetailsCompleted={userDetailsCompleted}
          setUserDetailsCompleted={setUserDetailsCompleted}
          userDetails={userDetails}
          setUserDetails={setUserDetails}
          steps={steps}
          customThemeBaseColor={customThemeBaseColor}
          translationInputFieldProps={translationInputFieldProps}
          onUpdateLocalCustomCopy={onUpdateLocalCustomCopy}
        />
      );
    }

    if (showPage(pages.READY)) {
      return (
        <div className={styles.parentContainer} key="page-content">
          <div className={styles.mainContainer}>
            <StepsProgress
              activeStep={campaign.askForSocioDemoWebSurvey ? '2' : '1'}
              steps={steps}
              customThemeBaseColor={customThemeBaseColor}
            />
            <img
              src={campaign.image}
              alt={campaign.image}
              className={styles.campaignImage}
            />
            <div
              className={styles.campaignImage}
              style={{ backgroundImage: `url('${campaign.image}')` }}
            />
            <div className={styles.campaignName}>{campaign.name}</div>
            <TranslationInputField
              name={customCopyFields.intro.name}
              translationKey={customCopyFields.intro.fields.INTRODUCTION}
              value={translations[
                customCopyFields.intro.fields.INTRODUCTION
              ].replace('{time}', Math.ceil(campaign.duration / 60))}
              customClasses={`${styles.readyLaunchDescription}`}
              {...translationInputFieldProps}
              onUpdateLocalCustomCopy={(key, value, name) =>
                onUpdateLocalCustomCopy(key, value, name)
              }
            />
            <div className={styles.acceptInputContainer}>
              <AcceptTermsInput
                name="agree-to-tc"
                label={{
                  I_AM_AWARE_OF_THE:
                    strings[selectedLanguage].I_AM_AWARE_OF_THE,
                  TERMS_AND_CONDITIONS:
                    strings[selectedLanguage].TERMS_AND_CONDITIONS,
                  AND_AGREE_BY_CHECKING_THIS_BOX:
                    strings[selectedLanguage].AND_AGREE_BY_CHECKING_THIS_BOX,
                  AND_CONFIRM_I_AGREE:
                    strings[selectedLanguage].AND_CONFIRM_I_AGREE,
                  I_AGREE: strings[selectedLanguage].I_AGREE
                }}
                active={termsAccepted}
                onClick={() => setTermsAccepted(!termsAccepted)}
                setViewTerms={setViewTerms}
                customThemeBaseColor={customThemeBaseColor}
              />
            </div>
          </div>
          <div>
            <Footer
              onClickNext={() => termsAccepted && startCampaignQueestions()}
              disabledNext={!termsAccepted}
              nextLabel={translations.NEXT}
              page={campaign && result && campaignStarted ? pages.READY : null}
              pages={pages}
              onClickBack={() => {
                setUserDetailsCompleted(false);
                if (!campaign.askForSocioDemoWebSurvey)
                  setCampaignStarted(false);
              }}
              backLabel={translations.BACK}
              hideIllustrationsInWebSurvey={
                campaign.hideIllustrationsInWebSurvey && campaign.customBranding
              }
              customThemeBaseColor={customThemeBaseColor}
            />
          </div>
        </div>
      );
    }
  }

  return null;
};
